import * as z from "zod";
import { flag, pagination } from "../index";
import { externalSystems } from "../til";

// The codes the other systems give the reference data (`platform.code_mapping`, ADR-064, ADR-077): what a translator
// puts in a message in place of a Nexxt id. A record has one code per system, for all brands or for one brand.

/** What a translator asks a code for: `domain` of `platform.code_mapping`. */
export const mappingDomains = [
  "brand",
  "season",
  "gender",
  "product_category",
  "product_subcategory",
  "size_run_category",
  "size_run",
  "size",
  "colour",
  "customs_classification",
  "price_list",
  "weight_unit",
  "supplier",
  "location",
  "payment_terms",
  "delivery_term",
] as const;
export type MappingDomain = (typeof mappingDomains)[number];

const scope = {
  system: z.enum(externalSystems),
  domain: z.enum(mappingDomains),
  /** The brand the codes are for; left out for the codes of every brand. */
  brandOrgId: z.uuid().optional(),
};

/** The records of a domain with the code a system gives each, by name. */
export const listCodeMappingsInput = z.object({
  ...scope,
  /** Only the records still without a code. */
  unmapped: flag.optional(),
  /** Part of the name of a record, or of its code. */
  q: z.string().trim().min(1).max(100).optional(),
  ...pagination({ defaultSize: 100, maxSize: 500 }),
});
export type ListCodeMappingsInput = z.input<typeof listCodeMappingsInput>;

export const codeMappingDto = z
  .object({
    internalId: z.uuid(),
    /** The record in a few words. */
    label: z.string(),
    /** Null: no code yet, and a message that names the record waits (ADR-064). */
    externalCode: z.string().nullable(),
    /** More than one code where a translator needs one: every code, until a save leaves one. */
    ambiguous: z.array(z.string()),
    /** The code for every brand, when the list is the one of a brand: what a translator uses without one of its own. */
    defaultCode: z.string().nullable(),
  })
  .meta({ id: "CodeMapping" });

const externalCode = z.string().trim().min(1).max(200);

/** The code of each record named: null takes the code away. A record takes one code, the others it had go. */
export const saveCodeMappingsInput = z.object({
  ...scope,
  mappings: z
    .array(z.object({ internalId: z.uuid(), externalCode: externalCode.nullable() }))
    .min(1)
    .max(1000)
    .refine((rows) => new Set(rows.map((row) => row.internalId)).size === rows.length),
});
export type SaveCodeMappingsInput = z.input<typeof saveCodeMappingsInput>;

export const savedCodeMappingsDto = z.object({ saved: z.number().int(), removed: z.number().int() });

/** How far the codes of a system are, by domain: for all brands, or for one. */
export const getCodeMappingSummaryInput = z.object({ system: scope.system, brandOrgId: scope.brandOrgId });
export type GetCodeMappingSummaryInput = z.input<typeof getCodeMappingSummaryInput>;

export const codeMappingSummaryDto = z
  .object({
    domains: z.array(
      z.object({ domain: z.enum(mappingDomains), records: z.number().int(), unmapped: z.number().int() }),
    ),
    /** The brands a list can be narrowed to. */
    brands: z.array(z.object({ orgId: z.uuid(), name: z.string() })),
  })
  .meta({ id: "CodeMappingSummary" });
